import { AppConfig, useAppConstantsContext } from '@ltvco/refresh-lib/ctx';
import {
  formatFamilyTreeDataFromDataCollection,
  formatDataCollectionRecordCard,
  PostIndi,
  AddToFamilyTreeButton,
} from '@ltvco/refresh-lib/fmt';
import {
  type DataCollectionRecord,
  type CollectionRecordType,
} from '@ltvco/refresh-lib/hooks';
import { useContext } from 'react';

import { useSearchPersonResultsContext } from 'routes/SearchPerson/context';

import { BaseCard } from '../BaseCard';
import {
  InfoSection,
  NameTypography,
  SecondaryTextTypography,
  SecondaryTextSubtitleTypography,
  CustomList,
} from '../common/components';
import { formatName } from '../common/utils';

export function MobileIndexCollectionCard({
  indexCollectionRecord,
  collectionName,
  recordInTree,
  handleAddToFamilyTree,
  collectionId,
  tabName,
}: {
  indexCollectionRecord: DataCollectionRecord;
  collectionName: string;
  recordInTree: boolean;
  // eslint-disable-next-line no-unused-vars
  handleAddToFamilyTree: (postIndi: PostIndi) => void;
  collectionId: CollectionRecordType;
  tabName?: string;
}) {
  const {
    images: { familyTreeImages },
    links: { baseUrl },
  } = useAppConstantsContext();
  const { isChatPage } = useSearchPersonResultsContext();
  const { trackEventGA4 } = useContext(AppConfig);

  const {
    firstName,
    middleName,
    lastName,
    country,
    state,
    county,
    city,
    estimatedBirthYear,
    estimatedBirthDate,
    mainDateLabel,
    mainDateText,
  } = formatDataCollectionRecordCard(indexCollectionRecord);

  const recordId = indexCollectionRecord.recordId;
  const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ');
  const locations = [country, state, county, city].filter(Boolean);
  const birthText = estimatedBirthYear || estimatedBirthDate;

  const onHandleAddToFamilyTree = () => {
    const postIndi = formatFamilyTreeDataFromDataCollection(
      indexCollectionRecord,
      collectionId
    );

    trackEventGA4({
      event_name: 'search_results_interaction',
      type: 'card click',
      action: 'family tree cta',
      interacted_from: tabName,
    });

    handleAddToFamilyTree(postIndi);
  };

  const handleOpenMainIndexCollectionReport = () => {
    trackEventGA4({
      event_name: 'search_results_interaction',
      type: 'card click',
      action: 'primary cta',
      interacted_from: tabName,
    });

    if (recordId && collectionId) {
      trackEventGA4({
        event_name: 'viewed_report',
        interacted_from: 'search_results',
        report_type: 'index',
      });
      if (isChatPage) {
        window.open(
          `${baseUrl}/collection/${collectionId}/record/${recordId}`,
          '_blank',
          'noopener,noreferrer'
        );
        return;
      }

      const windowObjectReference = window.open();
      // It's possible for the browser to block opening a new window, in which case windowObjectReference will be null.
      // In that case, we just redirect the current window.
      const windowTarget = windowObjectReference || window;

      windowTarget.location.href = `${baseUrl}/collection/${collectionId}/record/${recordId}`;
    }
  };

  const formattedName = formatName(fullName || '');

  return (
    <BaseCard
      name={fullName}
      isExpandable={true}
      onDetailsClick={handleOpenMainIndexCollectionReport}
      renderIcon={() => (
        <img
          src={familyTreeImages?.imgFamilyTreeDeathIndexIcon}
          alt="Death Index"
          width={36}
          height={36}
        />
      )}
      renderHeader={() => (
        <>
          <NameTypography variant="subtitle1" fontWeight="bold">
            {formattedName}
          </NameTypography>
          {collectionName && (
            <SecondaryTextTypography variant="body2">
              {collectionName}
            </SecondaryTextTypography>
          )}
          {birthText && mainDateLabel !== 'Date of Birth' && (
            <SecondaryTextTypography variant="body2">
              Born: {birthText || '-'}
            </SecondaryTextTypography>
          )}
          {mainDateLabel && mainDateText && (
            <SecondaryTextTypography variant="body2">
              {mainDateLabel}: {mainDateText}
            </SecondaryTextTypography>
          )}
        </>
      )}
      renderExpandedContent={() => (
        <InfoSection>
          <SecondaryTextSubtitleTypography variant="subtitle2">
            Locations
          </SecondaryTextSubtitleTypography>
          {locations && locations.length > 0 ? (
            <CustomList items={locations as string[]} />
          ) : (
            <SecondaryTextTypography variant="body2">-</SecondaryTextTypography>
          )}
          <AddToFamilyTreeButton
            variant="pill-outline"
            reportInFamilyTree={recordInTree}
            addRelativeToFamilyTree={onHandleAddToFamilyTree}
            isCreatingFamilyTree={false}
            sx={{ mb: 0, mt: 2.5, width: '100%' }}
          />
        </InfoSection>
      )}
      footerText={formattedName}
      tabName={tabName}
    />
  );
}
